import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Divider,
  ListItemButton,
  ListItemIcon,
  Typography,
  Grid,
} from "@mui/material";
import { Person, ExitToApp, CloseOutlined } from "@mui/icons-material";
import ThemeToggleButton from "../components/ThemeToggleButton";
import useUserData from "../hooks/useUserData";
import PropTypes from "prop-types";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";
import OwwLogo from "../Assets/owwtransparentlogo.png";
import Footer from "../components/Footer";

const Layout = () => {
  const navigate = useNavigate();
  const { data } = useUserData();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { i18n } = useTranslation();
  const [direction, setDirection] = useState(i18n.dir());
  const me = typeof data?.me === "string" ? JSON.parse(data?.me) : data?.me;

  useEffect(() => {
    const handleLanguageChange = () => setDirection(i18n.dir());
    i18n.on("languageChanged", handleLanguageChange);
    return () => i18n.off("languageChanged", handleLanguageChange);
  }, [i18n]);

  const handleLogout = () => {
    localStorage.removeItem("auth.app.eventegration.com");
    if (me?.eventId && me?.role)
      navigate(`/demo/login/${me?.eventId}/${me?.role}`);
    else navigate("/demo/login");
  };

  const toggleDrawer = (open) => () => setDrawerOpen(open);

  return (
    <Box
      dir={direction}
      sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      <AppBar
        position="sticky"
        sx={{
          // background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: "none",
          // zIndex: theme.zIndex.drawer + 1
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* <IconButton 
              onClick={() => navigate('/')} 
              
            > */}
            <img
              src={OwwLogo}
              alt="Logo"
              style={{
                height: isMobile ? "60px" : "100px",
                // filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
              }}
              onClick={() => navigate("/")}
            />
            {/* </IconButton> */}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LanguageSwitcher />
            <ThemeToggleButton />
            <IconButton
              onClick={toggleDrawer(true)}
              sx={{
                p: 1,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              }}
            >
              <Avatar
                src={me?.profile_pic || "./profile_pic.png"}
                sx={{
                  width: 40,
                  height: 40,
                  border: `2px solid ${theme.palette.secondary.main}`,
                }}
              >
                {me?.name?.[0]?.toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1 }}>
        <Outlet />
      </Box>
      <Box
        sx={{
          background: theme.palette.mode === "light" ? "#2a4b50" : "inherit",
          p: 2,
          textAlign: "center",
        }}
      >
        <Footer />
      </Box>

      <DrawerComponent
        anchor={direction === "rtl" ? "right" : "left"}
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        me={me}
        handleLogout={handleLogout}
        navigate={navigate}
        theme={theme}
      />
    </Box>
  );
};

const DrawerComponent = ({
  anchor,
  open,
  onClose,
  me,
  handleLogout,
  navigate,
  theme,
}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const direction = theme.direction;

  useEffect(() => {
    QRCode.toDataURL(`${me?.eventId},${me?.role},${me?.id}`)
      .then((url) => setQrCodeUrl(url))
      .catch(console.error);
  }, [me]);

  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 300,
          bgcolor: "background.paper",
          "& .MuiListItemText-root": {
            textAlign: direction === "rtl" ? "right" : "left",
          },
        },
      }}
    >
      <Box sx={{ p: 2, textAlign: "center" }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
          }}
        >
          <CloseOutlined />
        </IconButton>
        <Button
          variant="containedGreen"
          onClick={onClose}
          onKeyDown={onClose}
          style={{
            padding: 0,
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
          }}
        >
          <img
            src={OwwLogo}
            alt="Event Logo"
            style={{
              height: 80,
              marginTop: 10,
              marginBottom: 2,
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
            }}
          />
        </Button>
        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Avatar
            src={me?.profile_pic || "./profile_pic.png"}
            sx={{
              width: 56,
              height: 56,
              border: `2px solid ${theme.palette.primary.main}`,
            }}
          >
            {me?.name?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ textAlign: "left" }}>
            <Typography variant="subtitle1" fontWeight="600">
              {me?.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {me?.email}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {me?.role}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            bgcolor: "background.default",
            borderRadius: 2,
            mb: 2,
            boxShadow: theme.shadows[1],
          }}
        >
          <img
            src={qrCodeUrl}
            alt="QR Code"
            style={{
              width: "100%",
              height: "auto",
              borderRadius: 8,
            }}
          />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
            {me?.id} | {me?.country?.toUpperCase()}
          </Typography>
        </Box>

        <List>
          <ListItemButton
            onClick={() => {
              navigate("/demo/profile");
              onClose();
            }}
            sx={{
              borderRadius: 1,
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            <ListItemIcon>
              <Person />
            </ListItemIcon>
            <ListItemText primary={t("Profile")} />
          </ListItemButton>

          <ListItemButton
            onClick={() => {
              handleLogout();
              onClose();
            }}
            sx={{
              borderRadius: 1,
              "&:hover": { backgroundColor: "action.hover" },
            }}
          >
            <ListItemIcon>
              <ExitToApp />
            </ListItemIcon>
            <ListItemText primary={t("Logout")} />
          </ListItemButton>
        </List>
      </Box>
    </Drawer>
  );
};

DrawerComponent.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  me: PropTypes.object,
  handleLogout: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  anchor: PropTypes.string.isRequired,
  theme: PropTypes.object.isRequired,
};

export default Layout;
